import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthShell, Field } from "../components/AuthShell";
import { API_BASE } from "../services/api";

function getApiError(data, fallback) {
  if (typeof data?.detail === "string") return data.detail;
  if (Array.isArray(data?.detail)) {
    return data.detail.map((item) => item.msg).filter(Boolean).join(", ") || fallback;
  }
  return fallback;
}

export default function SignUp() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    username: "",
    mobile_no: "",
    email: "",
    password: "",
    confirm_password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handle = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const requiredFields = [
      "first_name",
      "last_name",
      "username",
      "mobile_no",
      "email",
      "password",
      "confirm_password",
    ];
    if (requiredFields.some((field) => !form[field].trim())) {
      setError("Please fill in all fields.");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (form.password !== form.confirm_password) {
      setError("Passwords do not match.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/auth/signup`, {
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          username: form.username.trim(),
          mobile_no: form.mobile_no.trim(),
          email: form.email.trim(),
          password: form.password,
          confirm_password: form.confirm_password,
        }),
      });

      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(getApiError(data, "Unable to create account."));
      }

      navigate("/signin");
    } catch (err) {
      setError(err.message || "Unable to create account.");
    } finally {
      setLoading(false);
    }
  };

  const strength = form.password.length === 0 ? null : form.password.length < 8 ? "weak" : form.password.length < 12 ? "good" : "strong";
  const strengthColor = { weak: "bg-red-500", good: "bg-yellow-400", strong: "bg-green" }[strength] || "";
  const strengthW = { weak: "w-1/3", good: "w-2/3", strong: "w-full" }[strength] || "";

  return (
    <AuthShell
      title="Create an account"
      subtitle="Start visualizing your space in seconds"
      onSubmit={submit}
      error={error}
      loading={loading}
      submitLabel="Create account"
      footer={<>Already have an account? <Link to="/signin" className="text-accent2 hover:text-accent transition-colors">Sign in</Link></>}
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="First name" id="first_name" placeholder="Jane" value={form.first_name} onChange={handle} autoComplete="given-name" />
        <Field label="Last name" id="last_name" placeholder="Smith" value={form.last_name} onChange={handle} autoComplete="family-name" />
      </div>
      <Field label="Username" id="username" placeholder="krishna" value={form.username} onChange={handle} autoComplete="username" />
      <Field label="Mobile number" id="mobile_no" type="tel" placeholder="1234567890" value={form.mobile_no} onChange={handle} autoComplete="tel" />
      <Field label="Email" id="email" type="email" placeholder="you@example.com" value={form.email} onChange={handle} autoComplete="email" />

      {/* Password with strength */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="password" className="text-sm font-medium text-t1">Password</label>
        <input
          id="password" name="password" type="password" placeholder="Min. 8 characters"
          value={form.password} onChange={handle} autoComplete="new-password"
          className="bg-bg border border-white/[0.08] focus:border-accent/50 outline-none rounded-xl px-4 py-3 text-sm text-t1 placeholder-t3 transition-colors"
        />
        {strength && (
          <div className="flex items-center gap-3 mt-1">
            <div className="flex-1 h-1 bg-surface2 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-300 ${strengthColor} ${strengthW}`} />
            </div>
            <span className="text-xs text-t3 capitalize">{strength}</span>
          </div>
        )}
      </div>

      <Field
        label="Confirm password"
        id="confirm_password"
        type="password"
        placeholder="Repeat password"
        value={form.confirm_password}
        onChange={handle}
        autoComplete="new-password"
      />

      <p className="text-xs text-t3 -mt-1">
        By creating an account you agree to our{" "}
        <a href="#/" className="text-accent2 hover:underline">Terms</a> and{" "}
        <a href="#/" className="text-accent2 hover:underline">Privacy Policy</a>.
      </p>
    </AuthShell>
  );
}
